import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

/**
 * The Data Gateway's `get<Schema>s` queries take native `where` / `order` / `paging` arguments
 * instead of a single JSON-string `input: DynamicQueryInput` — see graphql-query-builder.ts for
 * why these are inlined as literals rather than passed as typed variables. Paged lists sort on the
 * server (`buildOrderLiteral`), so each page is a slice of the sorted whole.
 *
 * Employees with the latest overtime activity first by default, like the old list's grouped
 * `{ LastUpdateDate: -1 }` sort (an employee's newest overtime row); the balance row is touched on
 * every change, so its `LastUpdatedDate` is that moment.
 */
export const DEFAULT_OVERTIME_ORDER: OrderBy[] = [{ field: 'LastUpdatedDate', direction: 'DESC' }];

export const buildGetOvertimeQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_OVERTIME_ORDER
) => `
  query {
    Overtimes: getOvertimes(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        UserId
        ResourceName
        ResourceEmail
        DepartmentId
        DepartmentName
        DepartmentKey
        TotalTakenOvertimeHourInSecond
        TotalRequestedOvertimeHourInSecond
        TotalRemainingOvertimeHourInSecond
        TotalRemainingOvertimeInDays
        TotalOvertimeHourInSecond
        LifeTimeOverTime
      }
    }
  }
`;

/** An employee's ledger rows, newest day first (entries of one day newest first) by default. */
export const DEFAULT_OVERTIME_LEDGER_ORDER: OrderBy[] = [
  { field: 'AttendanceDate', direction: 'DESC' },
  { field: 'CreatedDate', direction: 'DESC' },
];

export const buildGetOvertimeLedgerQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_OVERTIME_LEDGER_ORDER
) => `
  query {
    OvertimeLedger: getOvertimes(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        ItemId
        UserId
        Source
        AttendanceDate
        TotalWorkingTimePerDay
        OverTime
        CumulativeOverTime
        Note
      }
    }
  }
`;
